<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Prep Task Form Data Loading...
  </div>

  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="submitForm">

      <b-row class="mt-4">
        <b-col>

          <b-form-group label="Name" :label-cols="2">
            <b-form-input
              id="name"
              v-model="task_data.name"
              :state="!$v.task_data.name.$invalid"
              type="text"
              placeholder="Task Name"
            />
            <b-form-invalid-feedback>
              Required
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Description" :label-cols="2">
            <b-form-input
              id="description"
              v-model="task_data.description"
              type="text"
              placeholder="Task Description"
            />
          </b-form-group>

          <b-form-group label="Result Type" :label-cols="2">
            <b-form-select
              id="result_type"
              v-model="task_data.result_type"
              :options="result_type_opts"
            >
              <template #first>
                <option :value="null" disabled>
                  Select
                </option>
              </template>
            </b-form-select>
          </b-form-group>

          <b-form-group label="Inspecific Scheduling" :label-cols="2">
            <b-form-select
              id="inspecific_scheduling"
              v-model="task_data.inspecific_scheduling"
              :options="inspecific_scheduling_opts"
            >
              <template #first>
                <option :value="null" disabled>
                  Select
                </option>
              </template>
            </b-form-select>
          </b-form-group>

        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <div class="mb-3">
            <div class="display-4 font-3xl text-left">
              Analyses
            </div>
          </div>

          <b-form-group label="Analyte" :label-cols="2">
            <b-form-select
              id="analyte"
              v-model="selected_analyte"
              :options="analyte_opts"
            >
              <template #first>
                <option :value="null" disabled>
                  Select Analyte
                </option>
              </template>
            </b-form-select>
          </b-form-group>

          <b-form-group label="Analyses" :label-cols="2">
            <div v-if="loading_analyses" class="text-muted mt-1">
              <i class="fal fa-spinner fa-spin" /> Analysis Options Loading...
            </div>

            <b-form-checkbox-group
              v-else-if="analysis_checkbox_opts && analysis_checkbox_opts.length"
              id="analyses"
              v-model="analysis_data"
              :options="analysis_checkbox_opts"
              stacked
            />

            <div v-else class="text-muted mt-1">
              No Analyses Found
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <div class="mb-3">
            <div class="display-4 font-3xl text-left">
              Instruments
            </div>
          </div>

          <b-form-checkbox-group
            id="instruments"
            v-model="instruments_data"
          >
            <b-form-checkbox
              v-for="(option, index) in instrument_opts"
              :key="index"
              :value="option.value"
              class="mb-3"
            >
              {{ option.text }}
            </b-form-checkbox>
          </b-form-checkbox-group>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <div class="mb-3">
            <div class="display-4 font-3xl text-left">
              Export Configuration
            </div>
          </div>

          <div class="p-3 bg-light">
            <b-form-group label="Export Type" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                The spreadsheet that the process uses to export & import result data
              </b-alert>
              <b-form-input
                id="sample_type"
                v-model="export_map_data.type"
                type="text"
                disabled
              />
            </b-form-group>
          </div>

          <div class="p-3">
            <b-form-group label="Sheet" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                The name of the spreadsheet's page/sheet that the task uses to export & import result data
              </b-alert>
              <b-form-input
                id="export_sheet"
                v-model="export_map_data.sheet"
                type="text"
              />
            </b-form-group>
          </div>

          <div class="p-3 bg-light">
            <b-form-group label="Export Column" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                The column that existing result data is injected into when generating an export spreadsheet
              </b-alert>
              <b-form-input
                id="column_export"
                v-model="export_map_data.column_export"
                type="text"
              />
            </b-form-group>
          </div>

          <div class="p-3">
            <b-form-group label="Import Column" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                The column that result data is pulled from when importing a spreadsheet back into ELM
              </b-alert>
              <b-form-input
                id="column_import"
                v-model="export_map_data.column_import"
                type="text"
              />
            </b-form-group>
          </div>

          <div class="p-3 bg-light">
            <b-form-group label="Import Inputs" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                Additional column data points that are needing in ELM and are considered 'inputs' to the result
              </b-alert>

              <div v-for="(item, index) in export_map_data.import_inputs" :key="index" class="p-3 mb-3" :class="{'bg-white': index % 2 == 0}">
                <b-form-group label="Column" :label-cols="2">
                  <b-form-input
                    id="column"
                    v-model="item.column"
                    type="text"
                  />
                </b-form-group>

                <b-form-group label="Analysis" :label-cols="2">
                  <b-form-select
                    id="analysis_id"
                    v-model="item.analysis_id"
                    :options="analysis_opts"
                  >
                    <template #first>
                      <option :value="null" disabled>
                        Select Analysis
                      </option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </div>

              <b-button block variant="success" @click="addRow">
                <i class="fa fa-plus" /> Add Row
              </b-button>
            </b-form-group>
          </div>
        </b-col>
      </b-row>

      <!-- validation error -->
      <b-row v-if="invalid" class="mt-4">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-button
            type="submit"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            <i class="fal fa-save" /> Save
          </b-button>
        </b-col>
      </b-row>

    </b-form>
  </div>
</template>

<script>
import { getCollection } from '@/shared/fetch-utils'
import { required } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate/src'
import Multiselect from 'vue-multiselect/src/Multiselect'

export default {
  name: 'WorkflowPrepTaskForm',
  components: {
    Multiselect
  },
  mixins: [validationMixin],
  props: {
    sample_type: {
      type: Object,
      default: () => null
    },
    prep_task: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      loading: true,
      loading_analyses: false,

      task_data: {
        name: null,
        description: null,
        group: null,
        inspecific_scheduling: null,
        result_type: null
      },

      selected_analyte: null,
      analysis_data: [],

      instruments_data: [],

      export_map_data: {
        type: null,
        sheet: null,
        column_export: null,
        column_import: null,
        import_inputs: [
          {
            column: null,
            analysis_id: null
          }
        ]
      },

      analysis: null,
      instruments: null,

      result_type_opts: [
        { text: 'Final Result', value: 'FINAL_RESULT' },
        { text: 'Result Input', value: 'RESULT_INPUT' }
      ],

      inspecific_scheduling_opts: [
        { text: 'Yes', value: 1 },
        { text: 'No, must be scheduled to specific instrument instance', value: 0 }
      ]
    }
  },
  validations: {
    task_data: {
      name: { required }
    }
  },
  computed: {
    invalid() {
      return this.$v && this.$v.$dirty && this.$v.$invalid
    },

    analyte_opts () {
      return this.sample_type && this.sample_type.analytes ? this.sample_type.analytes.map(({ id, name }) => {
        return {
          value: id,
          text: name
        }
      }).sort((a, b) => a.text.localeCompare(b.text)) : []
    },

    instrument_opts () {
      return this.instruments && this.instruments.models.map(({ id, name }) => {
        return {
          value: id,
          text: name
        }
      }).sort((a, b) => a.text.localeCompare(b.text))
    },

    analysis_opts () {
      return this.analysis && this.analysis.models.map(({ id, name }) => {
        return {
          value: id,
          text: name
        }
      })
    },

    analysis_checkbox_opts () {
      return this.analysis && this.selected_analyte ? this.analysis.models.filter(({ analyte_id }) => analyte_id == this.selected_analyte).map(({ id, name }) => {
        return {
          value: id,
          text: name
        }
      }) : []
    }
  },
  async mounted () {
    this.loading = true

    this.instruments = await getCollection({
      collection_type: 'instrument',
      limit: 'all',
      message: this.$bvToast
    })

    this.fetchAnalyses()

    this.init()
  },
  methods: {
    initFormData () {
      if (this.prep_task && this.prep_task.id) {
        _.keys(this.task_data).forEach((key) => {
          if (this.prep_task[key] !== undefined) {
            this.task_data[key] = this.prep_task[key]
          }
        })

        if (this.prep_task.analyses && this.prep_task.analyses.length) {
          this.selected_analyte = this.prep_task.analyses[0].analyte_id

          this.analysis_data = this.prep_task.analyses.map(({ id }) => id)
        }

        this.instruments_data = this.prep_task.instruments.map(({ id }) => id)

        if (this.prep_task.export_map) {
          this.export_map_data = {
            ...this.prep_task.export_map,
            sheet: this.prep_task.export_map.sheet[0],
            import_inputs: this.prep_task.export_map.import_inputs && this.prep_task.export_map.import_inputs.length
              ? this.prep_task.export_map.import_inputs : []
          }
        }
      }
    },

    init () {
      if (this.sample_type && !this.prep_task) {
        this.export_map_data.type = _.lowerCase(this.sample_type.name)
      }

      this.initFormData()
      this.loading = false
    },

    addRow () {
      this.export_map_data.import_inputs.push({
        column: null,
        analysis_id: null
      })
    },

    async fetchAnalyses () {
      if (this.sample_type && this.sample_type.id) {
        this.loading_analyses = true

        this.analysis = await getCollection({
          collection_type: 'analysis',
          filters: [
            {
              logic: 'filterEquals',
              field: 'analyte.sample_type_id',
              value: this.sample_type.id
            }
          ],
          limit: 'all',
          message: this.$bvToast
        })

        this.loading_analyses = false
      }
    },

    submitForm () {
      this.$v.$touch()
      if (this.invalid) return

      return this.$emit('submit', {
        ...this.task_data,
        export_map: {
          ...this.export_map_data,
          sheet: [
            this.export_map_data.sheet
          ]
        },

        relations: {
          analyses: this.analysis_data && this.analysis_data.length ? this.analysis_data : undefined,
          instruments: this.instruments_data && this.instruments_data.length ? this.instruments_data : undefined,
        }
      })
    }
  }
}
</script>
